import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";

import { WorksApi, type WorkBulkImportResult } from "../api/works";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Called once after a successful import so the list page refreshes. */
  onImported: () => void;
};

export default function UploadWorksModal({ isOpen, onClose, onImported }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<WorkBulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset all state when reopened.
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (isOpen) {
      setFile(null);
      setResult(null);
      setError(null);
    }
  }

  // Clear the native file input so re-selecting the same file re-fires onChange.
  useEffect(() => {
    if (isOpen && fileInputRef.current) fileInputRef.current.value = "";
  }, [isOpen]);

  const canSubmit = !!file && !busy && !result;

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const csv = await file.text();
      const r = await WorksApi.bulkImport({ csv });
      setResult(r);
      if (r.imported.length > 0) onImported();
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? JSON.stringify(
              (e as { response?: { data?: unknown } }).response?.data ?? e,
            )
          : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={!busy}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import works from CSV</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {result ? (
            <ImportSummary result={result} />
          ) : (
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>CSV file</FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  p={1}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Headers (case-insensitive, order-independent). Required:{" "}
                  <code>title</code>, <code>type</code>, <code>format</code>,{" "}
                  <code>evidence_reason</code>, plus one of <code>creator_email</code>,{" "}
                  <code>creator_name</code>, <code>creator_id</code>,{" "}
                  <code>profile_id</code>. Optional: <code>description</code>,{" "}
                  <code>image_url</code> (https or s3), <code>evidence_data</code>,{" "}
                  <code>cert_type</code>. Max 1000 rows.
                </Text>
                <Text fontSize="xs" color="gray.600" mt={2}>
                  <b>type</b> is one of: AUDIO, DIGITAL_ART, OTHER, PHOTOGRAPH,
                  VIDEO, WRITTEN_WORD.
                </Text>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  <b>format</b> examples: book, manuscript, google_doc, isbn, daw,
                  m4a, mp3, wav, jpeg, png, tiff.
                </Text>
              </FormControl>
              {error && (
                <Alert status="error">
                  <AlertIcon />
                  <Box fontSize="sm">{error}</Box>
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {result ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <HStack>
              <Button variant="ghost" onClick={onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={submit}
                isLoading={busy}
                isDisabled={!canSubmit}
              >
                Import
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ImportSummary({ result }: { result: WorkBulkImportResult }) {
  return (
    <VStack align="stretch" spacing={3}>
      <Alert status={result.imported.length > 0 ? "success" : "info"}>
        <AlertIcon />
        Imported {result.imported.length}, errors {result.errors.length}, images
        not imported {result.failed_images.length}.
      </Alert>
      {result.errors.length > 0 && (
        <Box>
          <Text fontWeight="semibold" mb={1}>
            Errors
          </Text>
          <Box as="ul" pl={5} fontSize="sm">
            {result.errors.map((e, i) => (
              <li key={i}>
                row {e.row} — {e.reason}
                {e.fields?.length ? ` (${e.fields.join(", ")})` : ""}
                {e.value ? `: ${e.value}` : ""}
              </li>
            ))}
          </Box>
        </Box>
      )}
      {result.failed_images.length > 0 && (
        <Box>
          <Text fontWeight="semibold" mb={1}>
            Images not imported (work still created)
          </Text>
          <Box as="ul" pl={5} fontSize="sm">
            {result.failed_images.map((f, i) => (
              <li key={i}>
                row {f.row} — {f.reason} ({f.image_url})
              </li>
            ))}
          </Box>
        </Box>
      )}
    </VStack>
  );
}
